import React, { useState } from 'react';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
// "Bossa Antigua" Kevin MacLeod (incompetech.com)
// Licensed under Creative Commons: By Attribution 4.0 License
// http://creativecommons.org/licenses/by/4.0/
import cafeMusic from '../assets/cafe-bossa.mp3';
import './MusicPlayer.css';

const MusicPlayer: React.FC = () => {
  const [audioState, audioControls] = useAudioPlayer(cafeMusic);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const volume = parseFloat(event.target.value);
    audioControls.setVolume(volume);
  };

  const handleToggleMute = () => {
    if (audioState.isMuted) {
      audioControls.unmute();
    } else {
      audioControls.mute();
    }
  };

  const handleToggleEnabled = () => {
    if (audioState.isEnabled) {
      audioControls.disable();
    } else {
      audioControls.enable();
    }
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!audioState.isEnabled) {
    return (
      <div className="music-player disabled">
        <button
          className="music-toggle-btn"
          onClick={handleToggleEnabled}
          title="Enable background music"
          aria-label="Enable background music"
        >
          🎵
        </button>
      </div>
    );
  }

  return (
    <div className="music-player">
      <div className="music-controls">
        {/* メイン再生/一時停止ボタン */}
        <button
          className={`music-btn ${audioState.isPlaying ? 'playing' : 'paused'}`}
          onClick={audioControls.toggle}
          disabled={audioState.isLoading}
          title={audioState.isPlaying ? 'Pause music' : 'Play music'}
          aria-label={audioState.isPlaying ? 'Pause music' : 'Play music'}
        >
          {audioState.isLoading ? (
            <span className="loading-spinner">⏳</span>
          ) : audioState.isPlaying ? (
            '⏸️'
          ) : (
            '▶️'
          )}
        </button>

        {/* 音量コントロール */}
        <div
          className="volume-control"
          onMouseEnter={() => setShowVolumeSlider(true)}
          onMouseLeave={() => setShowVolumeSlider(false)}
        >
          <button
            className="volume-btn"
            onClick={handleToggleMute}
            title={audioState.isMuted ? 'Unmute' : 'Mute'}
            aria-label={audioState.isMuted ? 'Unmute' : 'Mute'}
          >
            {audioState.isMuted ? '🔇' : audioState.volume > 0.5 ? '🔊' : audioState.volume > 0 ? '🔉' : '🔈'}
          </button>

          {showVolumeSlider && (
            <div className="volume-slider-container">
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={audioState.volume}
                onChange={handleVolumeChange}
                className="volume-slider"
                aria-label="Volume"
              />
              <span className="volume-percentage">
                {Math.round(audioState.volume * 100)}%
              </span>
            </div>
          )}
        </div>

        {/* 無効化ボタン */}
        <button
          className="disable-btn"
          onClick={handleToggleEnabled}
          title="Disable background music"
          aria-label="Disable background music"
        >
          ❌
        </button>
      </div>

      {/* 進捗表示 */}
      {audioState.isEnabled && audioState.duration > 0 && (
        <div className="music-progress">
          <span className="time-display">
            {formatTime(audioState.currentTime)} / {formatTime(audioState.duration)}
          </span>
        </div>
      )}

      {/* エラー表示 */}
      {audioState.error && (
        <div className="music-error" role="alert">
          {audioState.error}
        </div>
      )}
    </div>
  );
};

export default MusicPlayer;